import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api.js';
import { PageHeader, Section } from '@/components/PageHeader.jsx';
import { Button } from '@/components/ui/Button.jsx';
import { Input, FormField, Textarea } from '@/components/ui/Input.jsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/Dialog.jsx';
import { Badge } from '@/components/ui/Badge.jsx';
import { DataTable } from '@/components/DataTable.jsx';
import { EmployeePicker } from '@/components/EmployeePicker.jsx';
import { useToast } from '@/components/ui/Toast.jsx';
import { formatLKR, rupeesToCents } from '@/lib/format.js';
import { Plus, X, Trash2 } from 'lucide-react';

export default function AdvancesPage() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const now = new Date();
  const [draft, setDraft] = useState({ employee_id: '', amount: 0, recover_year: now.getFullYear(), recover_month: now.getMonth() + 1, months: 1, reason: '' });

  const { data = [], isLoading } = useQuery({ queryKey: ['advances', {}], queryFn: () => api.get('/advances') });

  const create = useMutation({
    mutationFn: () => api.post('/advances', {
      employee_id: Number(draft.employee_id),
      amount_cents: rupeesToCents(draft.amount),
      recover_year: Number(draft.recover_year),
      recover_month: Number(draft.recover_month),
      months: Number(draft.months),
      reason: draft.reason || null,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['advances', {}] }); setOpen(false); toast({ variant: 'success', title: 'Advance issued' }); },
    onError: (e) => toast({ variant: 'error', title: 'Failed', description: e.message }),
  });
  const cancel = useMutation({ mutationFn: (id) => api.post(`/advances/${id}/cancel`), onSuccess: () => qc.invalidateQueries({ queryKey: ['advances', {}] }) });
  const del = useMutation({ mutationFn: (id) => api.del(`/advances/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey: ['advances', {}] }) });

  const columns = [
    { key: 'emp_no', header: 'Emp No', className: 'font-mono', sortable: true },
    { key: 'full_name', header: 'Name', sortable: true },
    { key: 'amount_cents', header: 'Amount', accessor: (r) => r.amount_cents, render: (r) => formatLKR(r.amount_cents), className: 'text-right tabular-nums font-mono', headClassName: 'text-right' },
    { key: 'balance_cents', header: 'Balance', accessor: (r) => r.balance_cents, render: (r) => formatLKR(r.balance_cents), className: 'text-right tabular-nums font-mono font-semibold', headClassName: 'text-right' },
    { key: 'recover', header: 'Recover From', render: (r) => `${r.recover_year}-${String(r.recover_month).padStart(2, '0')}`, className: 'font-mono' },
    { key: 'months', header: 'Months', className: 'text-right font-mono', headClassName: 'text-right' },
    { key: 'reason', header: 'Reason', className: 'font-mono' },
    { key: 'status', header: 'Status', render: (r) => <Badge variant={r.status === 'active' ? 'success' : 'default'}>{r.status}</Badge> },
    { key: '__a', header: '', className: 'w-24 text-right',
      render: (r) => (
        <div className="flex justify-end gap-1">
          {r.status === 'active' && <Button variant="ghost" size="icon" onClick={() => cancel.mutate(r.id)}><X className="h-4 w-4 text-amber-600" /></Button>}
          <Button variant="ghost" size="icon" onClick={() => del.mutate(r.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
        </div>
      ) },
  ];

  return (
    <div className="space-y-5">
      <PageHeader title="Advances" description="Salary advances with multi-month recovery"
        actions={<Button onClick={() => setOpen(true)}><Plus className="h-4 w-4" />New Advance</Button>}
      />
      <Section><DataTable columns={columns} data={data} loading={isLoading} exportFilename="advances.csv" /></Section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Issue Advance</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <FormField label="Employee" required><EmployeePicker value={draft.employee_id} onChange={(v) => setDraft({ ...draft, employee_id: v })} /></FormField>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Amount (LKR)" required><Input type="number" step="0.01" value={draft.amount} onChange={(e) => setDraft({ ...draft, amount: e.target.value })} /></FormField>
              <FormField label="Recovery Months"><Input type="number" min="1" value={draft.months} onChange={(e) => setDraft({ ...draft, months: e.target.value })} /></FormField>
              <FormField label="Recover Year"><Input type="number" value={draft.recover_year} onChange={(e) => setDraft({ ...draft, recover_year: e.target.value })} /></FormField>
              <FormField label="Recover Month"><Input type="number" min="1" max="12" value={draft.recover_month} onChange={(e) => setDraft({ ...draft, recover_month: e.target.value })} /></FormField>
            </div>
            <FormField label="Reason"><Textarea value={draft.reason} onChange={(e) => setDraft({ ...draft, reason: e.target.value })} /></FormField>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={() => create.mutate()} disabled={!draft.employee_id || create.isPending}>Issue</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
